import { Injectable, signal } from '@angular/core'

/**
 * The ThemeService is responsible for managing the application's theme settings.
 * It uses Angular signals to reactively update the theme and persists the selected theme in local storage.
 *
 * @description
 * This service initializes with a default theme ('light') and checks for a saved theme in local storage.
 * If a saved theme is found, it updates the theme signal. The service also provides a method to toggle
 * between 'light' and 'dark' themes, updating both the signal and local storage.
 */
@Injectable({
    providedIn: 'root',
})
export class ThemeService {
    /**
     * Signal for the current theme.
     */
    themeSignal = signal<string>('light')

    /**
     * Constructor for the ThemeService.

     * - Retrieves the saved theme from local storage.
     * - Initializes the `themeSignal` with the retrieved theme or the default 'light' theme.
     * - Persists the initial theme to local storage.
     */
    constructor() {
        const savedTheme = localStorage.getItem('theme')

        if (savedTheme) {
            this.themeSignal.update(() => savedTheme)
        } else {
            localStorage.setItem('theme', this.themeSignal())
        }
    }

    /**
     * Toggles the application theme between light and dark.

     * - Updates the `themeSignal` with the opposite theme.
     * - Persists the new theme to local storage.
     */
    updateTheme() {
        this.themeSignal.update((value) => (value === 'dark' ? 'light' : 'dark'))
        localStorage.setItem('theme', this.themeSignal())
    }
}
